export const SUBMISSION_FOOTER_EVENTS = {
    START: 'signals-submit:start',
    END: 'signals-submit:end',
    STEP: 'signals-submit:step',
    PREV: 'signals-submit:prev',
    NEXT: 'signals-submit:next',
    SUBMIT: 'signals-submit:submit',
    VALIDATION: 'signals-submit:validation',
};

function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) {
        el.setAttribute('hidden', '');
        el.setAttribute('aria-hidden', 'true');
    } else {
        el.removeAttribute('hidden');
        el.removeAttribute('aria-hidden');
    }
}

function setDisabled(el, disabled) {
    if (!el) return;
    el.disabled = !!disabled;
    if (disabled) {
        el.setAttribute('aria-disabled', 'true');
    } else {
        el.removeAttribute('aria-disabled');
    }
}

function setValidationBlocked(el, blocked) {
    if (!el) return;
    el.classList.toggle('is-validation-blocked', !!blocked);
    if (blocked) {
        el.setAttribute('aria-invalid', 'true');
    } else {
        el.removeAttribute('aria-invalid');
    }
}

function getPanelRoot(footer) {
    return (footer.closest && footer.closest('.nm-sheet, .nm-panel')) || document.body;
}

export function createSubmissionFooter(options) {
    const footer = options && options.footer;
    if (!footer) return null;

    const prevBtn = footer.querySelector('[data-submit-prev]');
    const nextBtn = footer.querySelector('[data-submit-next]');
    const sendBtn = footer.querySelector('[data-submit-send]');
    const stepLabel = footer.querySelector('#nm-submit-step-label');
    const panelRoot = getPanelRoot(footer);
    const defaultSendLabel = sendBtn ? sendBtn.textContent : '';
    const defaultNextLabel = nextBtn ? nextBtn.textContent : '';
    const validationNote = document.createElement('div');
    validationNote.className = 'nm-submit-validation-note';
    validationNote.setAttribute('role', 'alert');
    validationNote.hidden = true;
    footer.insertBefore(validationNote, footer.firstChild);
    let sendBusy = false;

    function dispatch(name, detail) {
        document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
    }

    function renderIdle() {
        setHidden(prevBtn, true);
        setHidden(nextBtn, true);
        setHidden(sendBtn, true);
        footer.setAttribute('data-submit-step', '0');
        footer.setAttribute('data-submit-total', '0');
        footer.removeAttribute('data-submit-mode');
        footer.classList.remove('is-listing');
        footer.classList.remove('is-last-step');
        validationNote.hidden = true;
        validationNote.textContent = '';
        validationNote.removeAttribute('data-tone');
        if (stepLabel) {
            stepLabel.textContent = '';
            stepLabel.hidden = true;
            stepLabel.removeAttribute('data-autosave-status');
        }
    }

    function renderState(state) {
        const step = Number((state && state.step) || 0);
        const total = Number((state && state.total) || 0);
        const valid = !state || state.valid !== false;
        const mode = state && state.mode ? String(state.mode) : '';
        const isListing = mode === 'listing';
        const isFirst = step <= 0;
        const isLast = total > 0 && step >= total - 1;

        validationNote.hidden = true;
        validationNote.textContent = '';
        validationNote.removeAttribute('data-tone');
        sendBusy = !!(state && state.submitting);

        setHidden(prevBtn, isFirst);
        setHidden(nextBtn, isLast);
        setHidden(sendBtn, !isLast);

        setDisabled(prevBtn, sendBusy);
        // Listing validation explains a blocked step after a click, so keep its
        // action button operable while still preventing navigation in the wizard.
        // Other submission flows retain their existing native disabled state.
        setDisabled(nextBtn, sendBusy || (!valid && !isListing));
        setDisabled(sendBtn, sendBusy || (!valid && !isListing));
        setValidationBlocked(nextBtn, !valid && isListing);
        setValidationBlocked(sendBtn, !valid && isListing);
        if (sendBtn) {
            sendBtn.textContent = state && state.submitLabel ? state.submitLabel : defaultSendLabel;
        }
        if (nextBtn) {
            nextBtn.textContent = state && state.nextLabel ? state.nextLabel : defaultNextLabel;
        }

        footer.setAttribute('data-submit-step', String(step));
        footer.setAttribute('data-submit-total', String(total));
        if (mode) footer.setAttribute('data-submit-mode', mode);
        else footer.removeAttribute('data-submit-mode');
        footer.classList.toggle('is-listing', isListing);
        footer.classList.toggle('is-last-step', total > 0 && isLast);

        if (stepLabel) {
            if (total > 0) {
                stepLabel.textContent = isListing && state && state.autosaveLabel
                    ? state.autosaveLabel
                    : (isListing ? 'Szkic' : (step + 1) + '/' + total);
                if (isListing) {
                    stepLabel.setAttribute('data-autosave-status', state && state.autosaveStatus ? state.autosaveStatus : 'idle');
                } else {
                    stepLabel.removeAttribute('data-autosave-status');
                }
                stepLabel.hidden = false;
            } else {
                stepLabel.textContent = '';
                stepLabel.hidden = true;
                stepLabel.removeAttribute('data-autosave-status');
            }
        }
    }

    function start(state) {
        panelRoot.classList.add('nm-panel--submitting');
        footer.classList.add('is-active');
        renderState(state || {});
    }

    function end() {
        sendBusy = false;
        if (sendBtn) {
            sendBtn.textContent = defaultSendLabel;
        }
        if (nextBtn) {
            nextBtn.textContent = defaultNextLabel;
        }
        panelRoot.classList.remove('nm-panel--submitting');
        footer.classList.remove('is-active');
        renderIdle();
    }

    function setSending(sending) {
        sendBusy = !!sending;
        setDisabled(sendBtn, sendBusy);
    }

    function showValidationMessage(detail) {
        detail = detail || {};
        const message = detail.message ? String(detail.message) : '';
        if (!message) {
            validationNote.hidden = true;
            validationNote.textContent = '';
            validationNote.removeAttribute('data-tone');
            return;
        }
        const tone = detail.tone === 'warning' ? 'warning' : 'error';
        const title = detail.title
            ? String(detail.title)
            : (tone === 'warning' ? 'Pole opcjonalne' : 'Uzupełnij pole');
        validationNote.setAttribute('data-tone', tone);
        validationNote.innerHTML = '<span class="nm-submit-validation-note__icon" aria-hidden="true">'
            + (tone === 'warning' ? '!' : '×')
            + '</span><span><strong></strong><small></small></span>';
        validationNote.querySelector('strong').textContent = title;
        validationNote.querySelector('small').textContent = message;
        validationNote.hidden = false;
    }

    if (prevBtn) {
        prevBtn.addEventListener('click', () => dispatch(SUBMISSION_FOOTER_EVENTS.PREV));
    }
    if (nextBtn) {
        nextBtn.addEventListener('click', () => dispatch(SUBMISSION_FOOTER_EVENTS.NEXT));
    }
    if (sendBtn) {
        sendBtn.addEventListener('click', () => {
            if (sendBusy) return;
            setSending(true);
            dispatch(SUBMISSION_FOOTER_EVENTS.SUBMIT);
        });
    }

    document.addEventListener(SUBMISSION_FOOTER_EVENTS.START, (ev) => {
        start(ev.detail || {});
    });
    document.addEventListener(SUBMISSION_FOOTER_EVENTS.STEP, (ev) => {
        renderState(ev.detail || {});
    });
    document.addEventListener(SUBMISSION_FOOTER_EVENTS.END, () => {
        end();
    });
    document.addEventListener(SUBMISSION_FOOTER_EVENTS.VALIDATION, (ev) => {
        showValidationMessage(ev.detail || {});
    });

    renderIdle();

    return { start, renderState, end, setSending };
}
